import {h, type FunctionalComponent} from '@stencil/core';
import {DashboardDocumentsTile} from '../dashbord-documents/dashboard-documents-tile';
import type {Strategies} from '../../../../../strategies/Strategies';
import type {ResultRenderer} from '../../../../../../utils/Renderer';
import {DashboardProductsList} from '../dashboard-products/dashboard-products-list/dashboard-products-list';
import {NoResults} from '../../no-results/no-results';
import type {
  SearchApiDocument,
  SearchDocument,
  SearchProduct,
} from '../../../../../../api/SearchApi';
import {capitalizeText, getAppName} from '../utils/utils';
import type {IEolDiscontinuedDetails} from '../../types/subcomponents-types.type';
import type {IWidgetRequestData} from '../../../../../widgets/offsites-widget/offsites-widget-v4/types/offsites-widget-v4.types';
import { SectionLoaderWrapper} from '../../../../section-loader/subcomponents/section-loader-wrapper';
import type {MiaFiltersDetails} from '../../../../../widgets/filter-related-widget/additional-conditions-banner/types';
import type {WCEventData} from '../../../../../../api/model/response/documents/SearchResult';

export interface IDashboardMobileView {
  apigeeAuth?: string;
  apigeeTokenUrl?: string;
  isDashboardView: boolean;
  reflectedTabs: Object;
  searcherNames: any;//eslint-disable-line @typescript-eslint/no-explicit-any
  strategies: Strategies;
  resultRenderer: ResultRenderer;
  resultClickAnalytics: CallableFunction;
  labelProvider: CallableFunction;
  country: string;
  language: string;
  tabName: string;
  isEcommerceMode: boolean;
  eShop: boolean;
  rtl: boolean;
  isOffsitesVisible: boolean;
  isExchangeVisible: boolean;
  widgetType: string;
  queryProp: string;
  hasCtaProductSelector: boolean;
  hasSupportButton: boolean;
  faqData;
  query: string;
  getTabCount: CallableFunction;
  relatedProductName?: string;
  smartCountrySelectorElement: HTMLElement | string;
  globalDecommissioning: boolean;
  totalHits: number;
  productCountrySelectorElement: HTMLElement | string;
  isProductNaCountrySelector?: boolean;
  productNaCountrySelectorLocales: string;
  analyticsTabName: string;
  dashboardButtonText: string;
  dashboardSubTitleText: string;
  lazyLoadImages?: boolean;
  brand: string;
  sectionsData?: {
    [key: string]:
      | SearchApiDocument[]
      | SearchDocument[]
      | SearchProduct[];
  };
  isSubstitutionTool?: boolean;
  eolDiscontinuedResults?: IEolDiscontinuedDetails;
  isConversationalSearchActive?: boolean;
  conversationalSearchProductsEndpoint?: string;
  conversationalSearchBrandUrl?: string;
  conversationalSearchMedaliaFormId?: string;
  offsiteBannersManagerRequestData?: IWidgetRequestData;
  offsitesViewOption?: string | undefined;
  tabs: Array<string>;
  isAdditionalConditionsWidgetActive?: boolean;
  isMiaPromotionWidget?: boolean;
  miaData?: MiaFiltersDetails;
  wcEventData?: WCEventData;
  isUnifiedCheckout?: boolean;
}

export const DashboardMobileView: FunctionalComponent<IDashboardMobileView> = (
  props
) => {
    const {
      apigeeAuth,
      apigeeTokenUrl,
      isDashboardView,
      reflectedTabs,
      searcherNames,
      strategies,
      resultRenderer,
      resultClickAnalytics,
      labelProvider,
      country,
      language,
      tabName,
      isEcommerceMode,
      eShop,
      rtl,
      isOffsitesVisible,
      isExchangeVisible,
      queryProp,
      hasCtaProductSelector,
      hasSupportButton,
      faqData,
      query,
      getTabCount,
      analyticsTabName,
      relatedProductName,
      smartCountrySelectorElement,
      globalDecommissioning,
      totalHits,
      productCountrySelectorElement,
      isProductNaCountrySelector,
      productNaCountrySelectorLocales,
      dashboardButtonText,
      dashboardSubTitleText,
      brand,
      sectionsData,
      isSubstitutionTool,
      eolDiscontinuedResults,
      isConversationalSearchActive,
      conversationalSearchProductsEndpoint,
      conversationalSearchBrandUrl,
      conversationalSearchMedaliaFormId,
      offsiteBannersManagerRequestData,
      lazyLoadImages,
      tabs,
      offsitesViewOption,
      isAdditionalConditionsWidgetActive,
      isMiaPromotionWidget,
      miaData,
      wcEventData,
      isUnifiedCheckout
    } = props;

    const dashboardState = {
      tab: searcherNames.all,
      ...reflectedTabs?.[searcherNames.all]
    };


    const sectionIsReady = (section: string):boolean => {
        const status = !reflectedTabs?.[section]?.ajaxIsLoading &&
          !reflectedTabs?.[section]?.hasError &&
          reflectedTabs?.[section]?.count !== undefined;

        return status;
    };

    const shouldRenderResults = (): boolean => {
        return Object.keys(searcherNames).some(section => reflectedTabs?.[searcherNames[section]]?.count > 0);
    };

    const renderNoResults =
    !reflectedTabs?.[searcherNames.all]?.ajaxIsLoading &&
    !reflectedTabs?.['se/all']?.hasError &&
    reflectedTabs?.[searcherNames.all]?.count === 0;

    const renderError =
    !reflectedTabs?.[searcherNames.all]?.ajaxIsLoading &&
    reflectedTabs?.[searcherNames.all]?.hasError;

    const hasProducts = tabs.includes('products');
    const hasDocuments = tabs.includes('documents');

    const targetBrand = getAppName(brand);
    const isProductData = sectionsData?.products?.length || eolDiscontinuedResults?.documents?.length;
    const tilesOrderArray = [
      'marketo',
      'software',
      'company',
      'blog',
      'services',
      'solutions',
      'support',
    ];

    const getSectionMinHeight = (
      loaded: boolean,
      data: number,
      dataLimit: number,
      minHeight: number
    ): number => {
        if (!loaded) return minHeight;
        const displayedElements = data > dataLimit ? dataLimit : data;
        return data > 0 ? displayedElements * 300 : minHeight;
    };

    const conversationalSearch = () => {
        return isConversationalSearchActive ? (
          <SectionLoaderWrapper
            hasSection={hasProducts}
            contentReady={sectionIsReady(searcherNames.products)}
            minHeight={82}
            noContent={sectionIsReady(searcherNames.products) ? isProductData === 0 : false}
          >
            <uiaas-search-widget-conversational-search-banner-mediator
              country={country}
              language={language}
              conversationalSearchProductsEndpoint={conversationalSearchProductsEndpoint}
              labelProvider={(e, f) => labelProvider(e, f)}
              apigeeAuth={apigeeAuth}
              apigeeTokenUrl={apigeeTokenUrl}
              query={query}
              conversationalSearchBrandUrl={conversationalSearchBrandUrl}
              conversationalSearchMedaliaFormId={conversationalSearchMedaliaFormId}
            />
          </SectionLoaderWrapper>
        ) : '';
    };

    const offsiteBanners = () => (
      <section>
        {offsiteBannersManagerRequestData ? (
          <uiaas-offsites-widget-v3
            isDashboard={true}
            isRtl={rtl}
            analyticsTabName={brand + '/new_all'}
            tabName={brand + '/all'}
            term={queryProp}
            offsiteBannersManagerRequestData={offsiteBannersManagerRequestData}
            labelProvider={(e, f) => labelProvider(e, f)}
            brand={brand}
            country={country}
            language={language}
          />
        ) : (
          ''
        )}
        {isOffsitesVisible && !offsitesViewOption ? (
          <uiaas-offsites-widget
            term={queryProp}
            country={country}
            language={language}
            is-dashboard-view={true}
            brand={brand}
            tab-name={tabName}
            analytics-tab-name={analyticsTabName}
            relatedProductName={relatedProductName}
          />
        ) : (
          ''
        )}
        {isOffsitesVisible && offsitesViewOption && !offsiteBannersManagerRequestData ? (
          <uiaas-offsites-widget-v4
            term={queryProp}
            country={country}
            language={language}
            is-dashboard-view={true}
            brand={brand}
            tab-name={tabName}
            analytics-tab-name={analyticsTabName}
            relatedProductName={relatedProductName}
          />
        ) : (
          ''
        )}
      </section>
    );

    const sectionIsLoading = (section: string):boolean => {
        if (!tabs.includes(section)) return false;
        if (reflectedTabs?.[searcherNames[section]]?.hasError) return false;
        return reflectedTabs?.[searcherNames[section]]?.count === undefined;
    };

    const getTileProps = (section: string) => {
        return {
          doc: sectionsData?.[section],
          title: labelProvider('tab.se/' + section, capitalizeText(section)),
          targetTab: searcherNames?.[section],
          tabCount: getTabCount(searcherNames?.[section]),
          useSkeleton: true,
          sectionIsLoading: sectionIsLoading(section),
          section,
          ...props
        };
    };

    return [
      renderError && (
        <uiaas-search-server-error
          query={queryProp}
          labelProvider={(e, f) => labelProvider(e, f)}
          reflectedState={dashboardState}
        />
      ),
      shouldRenderResults() ? <div class="dashboard-view">
        <div>
          {conversationalSearch()}
          {smartCountrySelectorElement}
          {productCountrySelectorElement}
          {isConversationalSearchActive ? '' : offsiteBanners()}
        </div>
        <SectionLoaderWrapper
          hasSection={hasProducts}
          contentReady={sectionIsReady(searcherNames.products)}
          minHeight={getSectionMinHeight(sectionIsReady(searcherNames.products), isProductData, 4, 1200)}
          noContent={sectionIsReady(searcherNames.products) ? isProductData === 0 : false} picturePlaceholder={true}>
          <section class={'dashboard__products'}>
            <DashboardProductsList
              docLimit={4}
              tabName={reflectedTabs?.[searcherNames?.products]?.tab}
              strategies={strategies}
              resultRenderer={resultRenderer}
              resultClickAnalytics={resultClickAnalytics}
              products={sectionsData?.products as SearchProduct[]}
              labelProvider={(e, f) => labelProvider(e, f)}
              country={country}
              language={language}
              isEcommerceMode={isEcommerceMode}
              eShop={eShop}
              rtl={rtl}
              targetTab={searcherNames.products}
              tabCount={reflectedTabs?.[searcherNames?.products]?.count}
              mobileView={true}
              isDashboardListVariant={false}
              isSubstitutionTool={isSubstitutionTool}
              eolDiscontinuedResults={eolDiscontinuedResults}
              lazyLoadImages={lazyLoadImages}
              isAdditionalConditionsWidgetActive={isAdditionalConditionsWidgetActive}
              isMiaPromotionWidget={isMiaPromotionWidget}
              miaData={miaData}
              term={query}
              wcEventData={wcEventData}
              isUnifiedCheckout={isUnifiedCheckout}
            />
          </section>
        </SectionLoaderWrapper>
        {isConversationalSearchActive ? offsiteBanners() : ''}
        <div class="dashboard__tiles dashboard-minus-margins">
          <SectionLoaderWrapper hasSection={hasCtaProductSelector} fullWidth={true} minHeight={150} >
            <div class="cta-product-selector-container widget-offset-bottom">
              <uiaas-search-widget-cta-product-selector
                app-name={targetBrand}
                country={country}
                language={language}
                is-dashboard-view={true}
                is-dashboard-mobile={true}
                dashboard-tab-name={tabName}
                analytics-tab-name={analyticsTabName}
              />
            </div>
          </SectionLoaderWrapper>
        </div>
        <SectionLoaderWrapper
          hasSection={hasDocuments}
          contentReady={sectionIsReady(searcherNames.documents)}
          minHeight={getSectionMinHeight(sectionIsReady(searcherNames.documents), sectionsData?.documents?.length, 2, 600)}
          noContent={ sectionIsReady(searcherNames.documents) ? sectionsData?.documents?.length === 0 : false}
          picturePlaceholder={true}
        >
          <div class='dashboard__tiles dashboard-card-minus-margins' >
            <DashboardDocumentsTile
              doc={sectionsData?.documents}
              labelProvider={(e, f) => labelProvider(e, f)}
              title={labelProvider('tab.se/documents', 'Documents')}
              targetTab={searcherNames.documents}
              tabCount={getTabCount(searcherNames.documents)}
              strategies={strategies}
              section="documents"
              country={country} language={language} query={query} rtl={rtl}/>
          </div>
        </SectionLoaderWrapper>
        <div class="dashboard__tiles dashboard-minus-margins">
          <SectionLoaderWrapper hasSection={hasSupportButton} fullWidth={true} minHeight={150}>
            <div class="cta-product-selector-container widget-offset-bottom">
              <uiaas-search-support-button
                app-name={targetBrand}
                country={country}
                language={language}
                is-dashboard-view="true"
                is-dashboard-mobile={true}
                dashboard-tab-name={tabName}
                analytics-tab-name={analyticsTabName}
              />
            </div>
          </SectionLoaderWrapper>
        </div>
        <div class="dashboard__tiles dashboard-minus-margins">
          <SectionLoaderWrapper
            hasSection={true}
            contentReady={sectionIsReady(searcherNames.faq)}
            noContent={sectionIsReady(searcherNames.faq) ? faqData.length === 0 : false}
            minHeight={250}>
            <div class={{'dashboard__faq':  faqData.length > 0}}>
              <uiaas-search-widget-faq
                country={country}
                language={language}
                rtl={rtl}
                maxItems={3}
                termAttribute={query}
                tabName={searcherNames.all}
                tabCount={getTabCount(searcherNames.faq)}
                targetTab={searcherNames.faq}
                dashboardSearcherData={faqData}
                dashboardButtonText={dashboardButtonText}
                dashboardSubTitleText={dashboardSubTitleText}
                isDashboardView={isDashboardView}
                labelProvider={(e, f) => labelProvider(e, f)}
              />
            </div>
          </SectionLoaderWrapper>
        </div>
        {tilesOrderArray.map((section) => (
          sectionsData?.[section] &&
            <SectionLoaderWrapper
              hasSection={tabs.includes(section)}
              contentReady={sectionIsReady(searcherNames[section])}
              minHeight={getSectionMinHeight(sectionIsReady(searcherNames[section]), sectionsData?.[section]?.length, 2, 600)}
              noContent={sectionIsReady(searcherNames[section]) ? sectionsData?.[section]?.length === 0 : false}
              picturePlaceholder={true}>
              <div class="dashboard__tiles dashboard-card-minus-margins">
                <DashboardDocumentsTile
                  doc={sectionsData?.[section]}
                  labelProvider={(e, f) => labelProvider(e, f)}
                  title={labelProvider(
                    section === 'marketo'
                      ? 'tab.dashboard.premium-content'
                      : 'tab.se/' + section,
                    section === 'marketo' ? 'Premium Content' : capitalizeText(section)
                  )}
                  targetTab={searcherNames?.[section]}
                  tabCount={getTabCount(searcherNames?.[section])}
                  strategies={strategies}
                  section={section === 'marketo' ? 'exclusive_content' : section} country={country} language={language} rtl={rtl} query={query}/>

              </div>
            </SectionLoaderWrapper>
        ))}
        <div class="dashboard__tiles dashboard-minus-margins">
          <SectionLoaderWrapper hasSection={isExchangeVisible} contentReady={sectionIsReady(searcherNames.all)} fullWidth={true} minHeight={200} >
            <div class="exchange-container widget-offset-bottom">
              <uiaas-search-widget-exchange-community
                termAttribute={queryProp}
                country={country}
                language={language}
                threshold={3}
                is-dashboard-view="true"
                screen="mobile"
                analytics-tab-name={analyticsTabName}
                strategies={getTileProps('support/community').strategies}
              />
            </div>
          </SectionLoaderWrapper>
        </div>
      </div> : renderNoResults ? (
        <div>
          <NoResults
            loading={reflectedTabs?.[searcherNames.all]?.ajaxIsLoading}
            labelProvider={(e, f) => labelProvider(e, f)}
            query={queryProp}
            globalDecommissioning={globalDecommissioning}
            language={language}
            country={country}
            isProductNaCountrySelector={isProductNaCountrySelector}
            productNaCountrySelectorLocales={productNaCountrySelectorLocales}
            totalHits={totalHits}
            analyticsTabName={analyticsTabName}
            tabName={tabName}
            brand={brand}
            rtl={rtl}
          />
        </div>
      ) : (
        ''
      )
    ];
};
